import { THUMB_SIZE_LIMIT } from '@constants';
import useToggle from '@hooks/useToggle';
import { ImageType } from '@types';
import dataURLtoFile from '@utils/dataURLtoFile';
import selectFile from '@utils/selectFile';
import { formatSize } from 'pano-utils';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import Button from './Button';
import Image from './Image';
import Modal from './Modal';
import { sendWarning } from './Noti';
import TextLink from './TextLink';
import Title from './Title';

interface Props {
  videoUrl: string;
  coverSrc?: string;
  coverType?: ImageType;
  title?: string;
  onSceneChangeClick?: () => void;
  onRequestClose: () => void;
  onConfirm: (file: File) => void;
}

const StyledModal = styled(Modal)`
  width: 950px;
  height: 600px;
`;

function VideoCaptureModal({
  videoUrl,
  coverSrc,
  coverType = ImageType.media,
  title = '视频全景图',
  onSceneChangeClick,
  onRequestClose,
  onConfirm,
}: Props) {
  const [dataUrl, setDataUrl] = useState<string>('');
  const loading = useToggle(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const cover = dataUrl || coverSrc;

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas
      .getContext('2d')!
      .drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataurl = canvas.toDataURL('image/jpeg');
    setDataUrl(dataurl);
  };

  const handleUpload = async () => {
    const files = await selectFile({
      accept: 'image/jpeg, image/png, image/gif',
    });
    const image = files[0];
    if (image.size > THUMB_SIZE_LIMIT) {
      sendWarning(`图片过大（不能大于${formatSize(THUMB_SIZE_LIMIT)})`);
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setDataUrl(e.target?.result as string);
    };
    reader.readAsDataURL(image);
  };

  const handleConfirm = async () => {
    if (!dataUrl) return;
    loading.on();
    const file = dataURLtoFile(dataUrl, 'video.jpg');
    onConfirm(file);
    onRequestClose();
  };

  return (
    <StyledModal
      isOpen
      header={<Title>{title}</Title>}
      footer={
        <Button disabled={!dataUrl || loading.isOn} onClick={handleConfirm}>
          确认操作
        </Button>
      }
      onRequestClose={onRequestClose}
    >
      <div className="flex h-full items-center justify-center">
        <div className="flex w-full px-35">
          <div className="flex-grow">
            <div className="mb-5 flex items-center font-medium">
              <div>选择合适的视角截屏作为封面</div>
              {!!onSceneChangeClick && (
                <TextLink
                  className="ml-auto"
                  onClick={() => {
                    onRequestClose();
                    onSceneChangeClick();
                  }}
                >
                  选择场景
                </TextLink>
              )}
            </div>
            <video
              crossOrigin="anonymous"
              ref={videoRef}
              controls
              disablePictureInPicture
              controlsList="nodownload"
              className="block h-300 w-full"
              src={videoUrl}
            />
            <Button size="lg" className="mt-10 w-full" onClick={handleCapture}>
              截 屏
            </Button>
          </div>
          <div className="ml-20 flex w-260 flex-shrink-0 flex-col">
            <div className="mb-5 font-medium">封面预览</div>
            {!!cover && (
              <div className="relative flex-1">
                {onSceneChangeClick ? (
                  <div className="relative flex h-260 w-260 justify-center">
                    <Image
                      className="h-full w-full"
                      src={cover}
                      type={dataUrl ? ImageType.image : coverType}
                    />
                    <div className="absolute bottom-30 flex items-center">
                      <Button onClick={handleUpload}>从本地上传</Button>
                      <div className="flex h-30 items-center justify-center rounded bg-black-black bg-opacity-50 px-10 text-sm text-white">
                        尺寸：512 x 512
                      </div>
                    </div>
                  </div>
                ) : (
                  <Image
                    src={cover}
                    className="w-260 h-260"
                    type={dataUrl ? ImageType.image : coverType}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </StyledModal>
  );
}

export default VideoCaptureModal;
